import React, { useState, useEffect } from 'react';
import { X, Save, Key, Lock, Eye, EyeOff, ShieldCheck, Loader2, Smartphone, Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const ChangePasswordModal = ({ isOpen, onClose }) => {
    const { t, i18n } = useTranslation(); // 🎯 تفعيل الترجمة
    const [loading, setLoading] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);

    // حالات الـ 2FA
    const [requires2FA, setRequires2FA] = useState(false);
    const [twoFactorMethod, setTwoFactorMethod] = useState('none');
    const [verificationCode, setVerificationCode] = useState('');
    const [userEmail, setUserEmail] = useState('');

    useEffect(() => {
        if (isOpen) {
            const user = JSON.parse(localStorage.getItem('user'));
            setUserEmail(user?.email || '');
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setRequires2FA(false);
        setVerificationCode('');
        setTwoFactorMethod('none');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();

        if (newPassword !== confirmPassword) {
            toast.error(t('changePassword.errMismatch')); // 🎯
            return;
        }

        if (newPassword.length < 6) {
            toast.error(t('changePassword.errLength')); // 🎯
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/user/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
                code: verificationCode || null
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.data?.status === 'requires_2fa') {
                setTwoFactorMethod(res.data.method);
                setRequires2FA(true);
                toast.success(t('changePassword.msgVerifyReq')); // 🎯
            } else {
                toast.success(t('changePassword.msgSuccess')); // 🎯
                handleClose();
            }
        } catch (err) {
            const errorMsg = err.response?.data?.detail || t('changePassword.errFailed'); // 🎯
            toast.error(errorMsg);
            if (requires2FA) setVerificationCode('');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-surface-card w-full max-w-md rounded-sm shadow-2xl border-t-4 border-primary overflow-hidden flex flex-col">

                {/* Header */}
                <div className="p-4 border-b flex justify-between items-center bg-surface-panel">
                    <div className="flex items-center gap-2">
                        <Key size={18} className="text-primary" />
                        <span className="font-black text-[12px] text-primary uppercase tracking-widest">{t('changePassword.title')}</span> {/* 🎯 */}
                    </div>
                    <button onClick={handleClose} className="text-ink-secondary hover:text-status-danger transition-colors"><X size={20}/></button>
                </div>

                {/* Body */}
                <div className="p-8">
                    {!requires2FA ? (
                        <form id="password-form" onSubmit={handleSubmit} className="space-y-5 animate-in slide-in-from-left-4 fade-in duration-300">

                            <div className="flex flex-col border-b border-line pb-1">
                                <label className="text-[9px] font-black text-ink-secondary uppercase tracking-tighter mb-1">{t('changePassword.currentPwd')}</label> {/* 🎯 */}
                                <div className="flex items-center">
                                    <Lock size={14} className="text-ink-secondary mx-2" />
                                    <input
                                        type={showCurrent ? "text" : "password"} required
                                        className="outline-none py-1 font-bold text-primary bg-transparent w-full text-sm tracking-widest"
                                        value={currentPassword}
                                        onChange={e => setCurrentPassword(e.target.value)}
                                    />
                                    <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="text-ink-secondary hover:text-primary mx-1">
                                        {showCurrent ? <EyeOff size={16}/> : <Eye size={16}/>}
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col border-b border-line pb-1 pt-4">
                                <label className="text-[9px] font-black text-primary uppercase tracking-tighter mb-1">{t('changePassword.newPwd')}</label> {/* 🎯 */}
                                <div className="flex items-center">
                                    <Key size={14} className="text-primary mx-2" />
                                    <input
                                        type={showNew ? "text" : "password"} required
                                        className="outline-none py-1 font-bold text-primary bg-transparent w-full text-sm tracking-widest"
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                    />
                                    <button type="button" onClick={() => setShowNew(!showNew)} className="text-ink-secondary hover:text-primary mx-1">
                                        {showNew ? <EyeOff size={16}/> : <Eye size={16}/>}
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col border-b border-line pb-1 pt-4">
                                <label className="text-[9px] font-black text-primary uppercase tracking-tighter mb-1">{t('changePassword.confirmPwd')}</label> {/* 🎯 */}
                                <div className="flex items-center">
                                    <ShieldCheck size={14} className="text-primary mx-2" />
                                    <input
                                        type="password" required
                                        className="outline-none py-1 font-bold text-primary bg-transparent w-full text-sm tracking-widest"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                        </form>
                    ) : (
                        <form id="password-form" onSubmit={handleSubmit} className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
                            <div className="flex flex-col items-center justify-center text-center space-y-3 mb-2">
                                <div className="w-14 h-14 bg-primary-soft rounded-full flex items-center justify-center text-primary shadow-inner">
                                    {twoFactorMethod === 'authenticator' ? <Smartphone size={28} /> : <Mail size={28} />}
                                </div>
                                <h3 className="font-black text-primary-dark text-sm uppercase tracking-tight">{t('changePassword.securityTitle')}</h3> {/* 🎯 */}
                                <p className="text-[10px] text-ink-secondary font-medium px-4">
                                    {twoFactorMethod === 'authenticator'
                                      ? t('changePassword.googleAuthMsg')
                                      : `${t('changePassword.emailOtpMsg')} (${userEmail})`} {/* 🎯 */}
                                </p>
                            </div>

                            <div>
                                <input
                                    type="text" required maxLength={6} autoFocus
                                    className="w-full border-2 border-line focus:border-primary rounded-sm py-2 text-center outline-none transition-colors text-xl font-mono tracking-[0.5em] text-primary"
                                    placeholder="------"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                />
                            </div>

                            <button type="button" onClick={() => setRequires2FA(false)} className="w-full text-[10px] uppercase font-bold text-ink-secondary hover:text-ink flex items-center justify-center gap-1">
                                <ArrowLeft size={12} className={i18n.language === 'ar' || i18n.language === 'ur' ? 'rotate-180' : ''} /> {t('changePassword.btnBack')} {/* 🎯 */}
                            </button>
                        </form>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-4 bg-surface-panel border-t flex justify-end gap-3 flex-shrink-0">
                    <button onClick={handleClose} className="px-6 py-2 text-[10px] font-black uppercase text-ink-secondary hover:bg-surface-card transition-all">{t('changePassword.btnCancel')}</button> {/* 🎯 */}
                    <button
                        type="submit" form="password-form" disabled={loading || (requires2FA && verificationCode.length !== 6)}
                        className="bg-primary-dark text-ink-inverse px-8 py-2 rounded-sm font-black text-[10px] uppercase flex items-center gap-2 hover:bg-primary shadow-lg transition-all disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
                        {requires2FA ? t('changePassword.btnVerify') : t('changePassword.btnUpdate')} {/* 🎯 */}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangePasswordModal;
